import { ChevronLeft, ChevronRight, MoreHorizontal } from "lucide-react";
import React, { useCallback, useMemo, useRef } from "react";
import { useCommonI18n } from "@/hooks/useModuleI18n";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";

interface PaginationProps {
  currentPage: number;
  totalCount: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  siblingCount?: number;
  showInfo?: boolean;
  compactMobile?: boolean;
  /**
   * Estado de carga real del padre. Permite distinguir "cargando" (congelar el
   * ultimo estado para evitar parpadeo) de "sin datos" (mostrar vacio). Sin
   * esta prop se usa la heuristica previa (congelar cuando totalCount===0), que
   * no distingue una sucursal vacia de una carga en progreso.
   */
  isLoading?: boolean;
  /**
   * `totalCount` es una cota inferior: el servidor dejó de contar al llenar su
   * ventana (paginación por índice). Se muestra como "120+".
   */
  totalCountIsLowerBound?: boolean;
}

// ─── Pure helpers (module-level, never re-created) ────────────────────────────

function range(start: number, end: number): number[] {
  const length = end - start + 1;
  return Array.from({ length }, (_, idx) => idx + start);
}

function getPaginationRange(
  currentPage: number,
  totalPages: number,
  siblingCount: number,
): (number | "...")[] {
  // Umbral alto para mostrar todas las paginas y mantener estable la
  // estructura entre cambios (evita el glitch de reordenamiento).
  const ALWAYS_SHOW_ALL_THRESHOLD = 15;
  if (totalPages <= ALWAYS_SHOW_ALL_THRESHOLD) return range(1, totalPages);

  const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
  const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPages);

  const shouldShowLeftDots = leftSiblingIndex > 2;
  const shouldShowRightDots = rightSiblingIndex < totalPages - 2;

  if (!shouldShowLeftDots && shouldShowRightDots) {
    const leftItemCount = 3 + 2 * siblingCount;
    return [...range(1, leftItemCount), "...", totalPages];
  }

  if (shouldShowLeftDots && !shouldShowRightDots) {
    const rightItemCount = 3 + 2 * siblingCount;
    return [1, "...", ...range(totalPages - rightItemCount + 1, totalPages)];
  }

  if (shouldShowLeftDots && shouldShowRightDots) {
    return [
      1,
      "...",
      ...range(leftSiblingIndex, rightSiblingIndex),
      "...",
      totalPages,
    ];
  }

  return [];
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function AdvancedPagination({
  currentPage,
  totalCount,
  pageSize = 5,
  onPageChange,
  siblingCount = 1,
  showInfo = true,
  compactMobile = false,
  isLoading,
  totalCountIsLowerBound = false,
}: PaginationProps) {
  const { t } = useCommonI18n();

  const totalPages = Math.ceil(totalCount / pageSize);

  // Recuerda el ultimo estado valido (con datos) para mantener la cantidad
  // exacta de botones estable durante recargas/loading donde el padre
  // pueda pasar totalCount=0 momentaneamente.
  const lastValidStateRef = useRef({
    totalCount,
    totalPages: Math.max(totalPages, 1),
    currentPage,
    isLowerBound: totalCountIsLowerBound,
  });

  // ¿Los valores actuales son "frescos" (ya cargados) o conviene congelar el
  // ultimo estado valido para evitar parpadeo durante la carga?
  // - Si el padre informa isLoading, es la fuente de verdad: distingue
  //   "cargando" de "sin datos" (una sucursal vacia NO debe mostrar el conteo
  //   de la sucursal anterior).
  // - Sin isLoading, heuristica previa: congelar cuando totalCount===0.
  const isLoadingKnown = typeof isLoading === "boolean";
  const isFresh = isLoadingKnown ? !isLoading : totalCount > 0;

  if (isFresh) {
    lastValidStateRef.current = {
      totalCount,
      totalPages: Math.max(totalPages, 1),
      currentPage,
      isLowerBound: totalCountIsLowerBound,
    };
  }

  const effective = isFresh
    ? {
        totalCount,
        totalPages: Math.max(totalPages, 1),
        currentPage,
        isLowerBound: totalCountIsLowerBound,
      }
    : lastValidStateRef.current;
  const plus = effective.isLowerBound ? "+" : "";
  const totalLabel = `${effective.totalCount}${plus}`;
  const totalPagesLabel = `${effective.totalPages}${plus}`;

  const effectiveTotalPages = effective.totalPages;
  // Acotar la pagina mostrada al rango valido: al cambiar a una sucursal con
  // menos (o cero) paginas, la pagina venida del padre/URL puede quedar fuera
  // de rango y mostrar "Mostrando 21-5 de 5".
  const effectiveCurrentPage = Math.min(
    Math.max(effective.currentPage, 1),
    effectiveTotalPages,
  );

  const paginationRange = useMemo(
    () =>
      getPaginationRange(
        effectiveCurrentPage,
        effectiveTotalPages,
        siblingCount,
      ),
    [effectiveCurrentPage, effectiveTotalPages, siblingCount],
  );

  const onNext = useCallback(() => {
    if (effectiveCurrentPage < effectiveTotalPages)
      onPageChange(effectiveCurrentPage + 1);
  }, [effectiveCurrentPage, effectiveTotalPages, onPageChange]);

  const onPrevious = useCallback(() => {
    if (effectiveCurrentPage > 1) onPageChange(effectiveCurrentPage - 1);
  }, [effectiveCurrentPage, onPageChange]);

  // ─── Early returns removed ──────────────────────────────────────────────
  // La paginacion siempre muestra la UI completa para mantener estabilidad
  // visual durante cambios de pagina y cargas. Los controles se deshabilitan
  // segun el estado (sin datos, primera/ultima pagina) en vez de ocultar nada.

  const startItem =
    effective.totalCount > 0 ? (effectiveCurrentPage - 1) * pageSize + 1 : 0;
  const endItem =
    effective.totalCount > 0
      ? Math.min(effectiveCurrentPage * pageSize, effective.totalCount)
      : 0;
  const hasNoData = effective.totalCount === 0;

  return (
    <Card className="w-full border-none p-0 rounded-none shadow-none">
      <CardContent className="p-0">
        {showInfo && (
          <div className="px-2 py-1 border-none">
            {compactMobile ? (
              // Modo compacto movil — solo controles centrados
              <div className="flex items-center justify-center px-1 py-1 gap-1">
                <Button
                  type="button"
                  onClick={onPrevious}
                  disabled={effectiveCurrentPage === 1}
                  variant="outline"
                  size="sm"
                  className="h-7 w-7 p-0 text-xs font-medium rounded-md"
                >
                  <ChevronLeft className="w-3 h-3" />
                </Button>

                <div className="flex items-center space-x-2">
                  <span className="text-xs text-gray-700 dark:text-slate-100">
                    {t("table.pagination.page", {
                      current: effectiveCurrentPage,
                      total: totalPagesLabel,
                    })}
                  </span>
                </div>

                <Button
                  type="button"
                  onClick={onNext}
                  disabled={
                    hasNoData || effectiveCurrentPage === effectiveTotalPages
                  }
                  variant="outline"
                  size="sm"
                  className="h-7 w-7 p-0 text-xs"
                >
                  <ChevronRight className="w-3 h-3" />
                </Button>
              </div>
            ) : (
              // Layout desktop completo con grid
              <div className="grid grid-cols-[1fr_auto_1fr] gap-4 items-center">
                {/* Columna Izquierda: informacion de elementos */}
                <div className="hidden md:flex items-center justify-start">
                  <span className="text-xs text-gray-700 dark:text-slate-100">
                    {hasNoData
                      ? t("table.pagination.noData")
                      : t("table.pagination.showingRange", {
                          start: startItem,
                          end: endItem,
                          total: totalLabel,
                        })}
                  </span>
                </div>

                {/* Columna Centro: controles de paginacion */}
                <div className="flex items-center justify-center px-1 py-1 gap-1">
                  <Button
                    type="button"
                    onClick={onPrevious}
                    disabled={effectiveCurrentPage === 1}
                    variant="outline"
                    size="sm"
                    className="h-7 w-7 p-0 text-xs font-medium rounded-md"
                  >
                    <ChevronLeft className="w-3 h-3" />
                  </Button>

                  {/* Numeros de pagina — solo sm+ */}
                  <div className="hidden sm:flex items-center space-x-1">
                    {paginationRange.map((pageNumber, index) =>
                      pageNumber === "..." ? (
                        <span
                          key={`dots-${index}`}
                          className="flex items-center justify-center w-7 h-7 text-gray-500 dark:text-gray-400"
                        >
                          <MoreHorizontal className="w-4 h-4" />
                        </span>
                      ) : (
                        <Button
                          key={pageNumber}
                          type="button"
                          onClick={
                            effectiveCurrentPage === pageNumber
                              ? undefined
                              : () => onPageChange(pageNumber as number)
                          }
                          variant={
                            effectiveCurrentPage === pageNumber
                              ? "subtle"
                              : "outline"
                          }
                          size="sm"
                          className="h-7 w-7 p-0 text-xs font-medium rounded-md transition-colors duration-200"
                        >
                          {pageNumber}
                        </Button>
                      ),
                    )}
                  </div>

                  {/* Pagina compacta — solo movil */}
                  <div className="flex sm:hidden items-center space-x-2">
                    <span className="text-xs text-gray-700 dark:text-slate-100">
                      {t("table.pagination.page", {
                        current: effectiveCurrentPage,
                        total: totalPagesLabel,
                      })}
                    </span>
                  </div>

                  <Button
                    type="button"
                    onClick={onNext}
                    disabled={
                      hasNoData || effectiveCurrentPage === effectiveTotalPages
                    }
                    variant="outline"
                    size="sm"
                    className="h-7 w-7 p-0 text-xs"
                  >
                    <ChevronRight className="w-3 h-3" />
                  </Button>
                </div>

                {/* Columna Derecha: ir a pagina */}
                <div className="hidden md:flex items-center justify-end space-x-2 text-xs text-gray-700 dark:text-slate-100">
                  <span>{t("table.pagination.goToPage")}</span>
                  <input
                    type="number"
                    min={1}
                    max={effectiveTotalPages}
                    value={effectiveCurrentPage}
                    onChange={(e) => {
                      const page = parseInt(e.target.value);
                      if (
                        page >= 1 &&
                        page <= effectiveTotalPages &&
                        page !== effectiveCurrentPage
                      ) {
                        onPageChange(page);
                      }
                    }}
                    className="w-16 px-2 py-1 border border-gray-300 rounded text-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:border-gray-600 dark:bg-gray-800 dark:text-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={hasNoData}
                  />
                  <span>
                    {t("table.pagination.of")} {totalPagesLabel}
                  </span>
                </div>
              </div>
            )}

            {/* Info movil stacked — oculto en modo compacto */}
            {!compactMobile && (
              <div className="md:hidden mt-2 flex flex-col gap-2">
                <div className="flex items-center justify-center text-xs text-gray-700 dark:text-slate-100">
                  <span>
                    {hasNoData
                      ? t("table.pagination.noData")
                      : `${t("table.pagination.showing")} ${startItem}-${endItem} ${t("table.pagination.of")} ${totalLabel}`}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}